import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer, MatDrawerMode } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Subject, Subscription, takeUntil } from 'rxjs';
import { PropertyService } from 'src/app/property/property.service';
import { AppState } from 'src/app/redux/app.state';
import { DeviceTypeEnum } from 'src/enums';
import { Brand, ExampleFlatNode, Product, Property, User } from 'src/typings';
import { CreateComponent } from '../create/create.component';
import { ProductService } from '../product.service';

@Component({
  selector: 'app-catalogue',
  templateUrl: './catalogue.component.html',
  styleUrls: ['./catalogue.component.scss']
})
export class CatalogueComponent implements OnDestroy {
  selectedProperties: { property: Property; values: string[] }[] = [];

  selectedBrands: Brand[] = [];

  available_brands: Brand[] = [];

  availableProps: { property: Property; values: string[] }[] = [];

  searchGroup: FormGroup = this.fb.group({
    payload: new FormControl(''),
    onlyInStock: new FormControl(false),
    withoutFolder: new FormControl(),
    withoutAddress: new FormControl(),
    withAddressDebt: new FormControl(),
    onlyBufferStock: new FormControl(),
    withoutPlacedStock: new FormControl(),
    multiCellOnly: new FormControl(),
    withoutImage: new FormControl(),
    withoutBarcode: new FormControl(),
    withoutProps: new FormControl()
  });

  user: User | undefined;

  @Input() type: string = 'catalogue';

  @Output() clearEvent: EventEmitter<void> = new EventEmitter();

  get searchControl() {
    return this.searchGroup.controls.payload as FormControl<string>;
  }

  get withoutPropsControl() {
    return this.searchGroup.controls.withoutProps as FormControl<boolean>;
  }

  get withoutBarcodeControl() {
    return this.searchGroup.controls.withoutBarcode as FormControl<boolean>;
  }

  get onlyInStockControl() {
    return this.searchGroup.controls.onlyInStock as FormControl<boolean>;
  }

  get withoutFolderControl() {
    return this.searchGroup.controls.withoutFolder as FormControl<boolean>;
  }

  get withoutAddressControl() {
    return this.searchGroup.controls.withoutAddress as FormControl<boolean>;
  }

  get withoutImageControl() {
    return this.searchGroup.controls.withoutImage as FormControl<boolean>;
  }

  get withAddressDebtControl() {
    return this.searchGroup.controls.withAddressDebt as FormControl<boolean>;
  }

  get onlyBufferStockControl() {
    return this.searchGroup.controls.onlyBufferStock as FormControl<boolean>;
  }

  get withoutPlacedStockControl() {
    return this.searchGroup.controls.withoutPlacedStock as FormControl<boolean>;
  }

  get multiCellOnlyControl() {
    return this.searchGroup.controls.multiCellOnly as FormControl<boolean>;
  }

  products: Product[] = [];

  resultLength: number = 0;

  pageLoaded: number = 0;

  loadingProducts: boolean = false;

  brandListLoading: boolean = false;

  propsListLoading: boolean = false;

  pageSize = 30;

  sort_active = 'description';

  sort_direction = 'asc';

  retailRange: number[] = [0, 100];

  selected_range_retail: number[] = [0, 100];

  @Input() selectedFolder: ExampleFlatNode | undefined;

  @ViewChild('drawerContainer') drawerElement: ElementRef | undefined;

  @ViewChild('leftBar') leftBar: MatDrawer | undefined;

  @ViewChild('rightBar') rightBar: MatDrawer | undefined;

  routeSubscription: Subscription;

  _searchControl: FormControl<string | null> = new FormControl('');

  searchEvent: EventEmitter<{ loadBrands: boolean; loadProps: boolean; loadCount: boolean }> =
    new EventEmitter();

  cacnelSearch$: Subject<boolean> = new Subject<boolean>();

  cacnelSearchBrands$: Subject<boolean> = new Subject<boolean>();

  cacnelSearchProps$: Subject<boolean> = new Subject<boolean>();

  cacnelSearchCount$: Subject<boolean> = new Subject<boolean>();

  searchEventSubscription: Subscription;

  searchServiceSubscription: Subscription | undefined;

  storeSubscription: Subscription | undefined;

  brandSubscription: Subscription | undefined;

  countSubscription: Subscription | undefined;

  propsSubscription: Subscription | undefined;

  loadCount: boolean = false;

  @Input() columnsToDisplay = [
    'id',
    'image',
    'description',
    'brand',
    'article',
    'analogies',
    'move',
    'retail',
    'stock',
    'cell',
    'selector'
  ];

  set deviceType(payload: DeviceTypeEnum) {
    this._deviceType = payload;

    switch (payload) {
      case DeviceTypeEnum.DESKTOP:
        this._drawerMode = 'side';
        this.columnsToDisplay = [
          'id',
          'image',
          'description',
          'brand',
          'article',
          'analogies',
          'retail',
          'stock',
          'cell',
          'move',
          'marketPlace',
          'selector'
        ];
        break;
      case DeviceTypeEnum.LAPTOP:
        this._drawerMode = 'over';
        this._filterOpen = false;
        this._folderOpen = false;
        this.columnsToDisplay = [
          'id',
          'image',
          'combineDescription',
          'analogies',
          'retail',
          'cell',
          'stock',
          'selector'
        ];
        break;
      case DeviceTypeEnum.TABLET:
        this._drawerMode = 'over';
        this._filterOpen = false;
        this._folderOpen = false;
        this.columnsToDisplay = [
          'image',
          'combineDescriptionWithId',
          'combineRetailWithStock',
          'analogies',
          'selector'
        ];
        break;
      case DeviceTypeEnum.MOBILE:
        this._drawerMode = 'over';
        this._filterOpen = false;
        this._folderOpen = false;
        this.columnsToDisplay = ['mobileRow'];
        break;
      default:
        this.columnsToDisplay = [
          'id',
          'image',
          'description',
          'brand',
          'article',
          'analogies',
          'retail',
          'stock',
          'cell',
          'selector'
        ];
        break;
    }
  }

  get deviceType() {
    return this._deviceType;
  }

  private _deviceType: DeviceTypeEnum = DeviceTypeEnum.DESKTOP;

  @Input() productsFormArray: FormArray | undefined;

  @Input() selectedProductsFormArray: FormArray | undefined;

  @Input() loadingProductsInTable: Product[] = [];

  @Output() removeAction: EventEmitter<Product> = new EventEmitter();

  private _folderOpen = true;

  get folderOpen() {
    if (this.type === 'selector') return false;
    return this._folderOpen;
  }

  private _filterOpen = true;

  get filterOpen() {
    if (this.type === 'selector') return false;
    return this._filterOpen;
  }

  private _drawerMode: MatDrawerMode = 'side';

  get drawerMode() {
    if (this.type === 'selector') return 'over';
    return this._drawerMode;
  }

  productFolderSubscription: Subscription;

  productBarcodeSubscription: Subscription;

  constructor(
    private productService: ProductService,
    private propertyService: PropertyService,
    private fb: FormBuilder,
    private activeRoute: ActivatedRoute,
    private store: Store<AppState>,
    private dialog: MatDialog
  ) {
    this.productBarcodeSubscription = this.productService.barcodeAddedToProduct.subscribe(
      (data) => {
        if (this.searchGroup.value.withoutBarcode === true) {
          const temp = this.products.filter((row) => row.id !== data.productId);
          this.products = temp;
        }
      }
    );
    this.productFolderSubscription = this.productService.folderAddedToProduct.subscribe((data) => {
      if (this.searchGroup.value.withoutFolder === true) {
        const temp = this.products.filter((row) => row.id !== data.productId);
        this.products = temp;
      }
    });
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      this.deviceType = data.user.deviceType;
      this._filterOpen =
        this.deviceType === DeviceTypeEnum.DESKTOP ? data.user?.info?.catalogueOpenFilter : false;
      this._folderOpen =
        this.deviceType === DeviceTypeEnum.DESKTOP ? data.user?.info?.catalogueOpenFolder : false;
    });
    this.searchEventSubscription = this.searchEvent.subscribe((data) => {
      this.search(data);
    });
    // this.searchGroup.valueChanges.subscribe(() => {
    //   this.searchEvent.emit({
    //     loadBrands: true,
    //     loadProps: true,
    //     loadCount: true
    //   });
    // });
    this.routeSubscription = this.activeRoute.params.subscribe((data) => {
      if (data.search) {
        this.searchControl.setValue(data.search);
        this.searchEvent.emit({
          loadBrands: true,
          loadCount: true,
          loadProps: true
        });
      }
    });
    window.addEventListener('searchSubmit', (e: any) => {
      this.searchControl.setValue(e.detail);
      this.searchEvent.emit({
        loadBrands: true,
        loadCount: true,
        loadProps: true
      });
    });
  }

  // ngOnInit() {
  //   if (!this.searchControl.value) {
  //     this.updateBrands();
  //     this.updateCount();
  //     this.loadProps();
  //   }
  // }

  @HostListener('document:keydown.alt.n', ['$event'])
  @HostListener('document:keydown.alt.т', ['$event'])
  openCreate(e: KeyboardEvent) {
    e.preventDefault();
    this.create();
  }

  @HostListener('document:keydown.alt.a', ['$event'])
  @HostListener('document:keydown.alt.ф', ['$event'])
  toggleFolder() {
    this.leftBar?.toggle();
  }

  @HostListener('document:keydown.alt.s', ['$event'])
  @HostListener('document:keydown.alt.ы', ['$event'])
  toggleFilter() {
    this.rightBar?.toggle();
  }

  ngOnDestroy() {
    this.routeSubscription.unsubscribe();
    this.searchEventSubscription.unsubscribe();
    this.searchServiceSubscription?.unsubscribe();
    this.brandSubscription?.unsubscribe();
    this.countSubscription?.unsubscribe();
    this.propsSubscription?.unsubscribe();
    this.productFolderSubscription.unsubscribe();
  }

  remove(product: Product) {
    this.removeAction.emit(product);
  }

  handleClearFolder() {
    this.selectedFolder = undefined;
    this.searchEvent.emit({
      loadBrands: true,
      loadProps: true,
      loadCount: true
    });
  }

  selectFolder(folder: ExampleFlatNode) {
    this.selectedFolder = folder;
    this.selectedBrands = [];
    this.selectedProperties = [];
    this.searchEvent.emit({
      loadBrands: true,
      loadProps: true,
      loadCount: true
    });
  }

  clearFolder() {
    this.selectedFolder = undefined;
  }

  onScroll(event: any) {
    if (this.products.length === 0) return;
    if (
      event.target.offsetHeight + event.target.scrollTop + 1 >= event.target.scrollHeight &&
      this.products.length < this.resultLength
    ) {
      this.searchByPage(this.pageLoaded);
    }
  }

  async search(
    payload: { loadBrands: boolean; loadProps: boolean; loadCount: boolean } = {
      loadBrands: true,
      loadProps: true,
      loadCount: true
    }
  ) {
    this.scrollTop();
    this.products = [];
    this.pageLoaded = 0;
    if (payload.loadBrands) this.updateBrands();
    if (payload.loadCount) this.updateCount();
    if (payload.loadProps) this.loadProps();
    this.searchByPage();
    return;
  }

  scrollTop() {
    this.drawerElement?.nativeElement.scrollTo({ top: 0 });
  }

  async updateCount() {
    this.loadCount = true;
    this.cacnelSearchCount$.next(true);
    this.countSubscription = this.productService
      .getCountSearch(this.buildSearchPayload({ limit: this.pageSize }))
      .pipe(
        map((resCount) => {
          this.resultLength = resCount.length;
        }),
        takeUntil(this.cacnelSearchCount$)
      )
      .subscribe(() => {
        this.loadCount = true;
      });
  }

  async updateBrands() {
    this.brandListLoading = true;
    this.cacnelSearchBrands$.next(true);
    this.brandSubscription = this.productService
      .getBrandListSearch(this.buildSearchPayload())
      .pipe(
        map((resBrandList) => {
          this.available_brands = resBrandList;
          const prevSelectedBrands = JSON.stringify(this.selectedBrands);
          this.selectedBrands = this.selectedBrands.filter((brand) => {
            const result = this.available_brands.find((_brand) => _brand.id === brand.id);
            return result;
          });

          const newSelectedBrands = JSON.stringify(this.selectedBrands);

          if (prevSelectedBrands !== newSelectedBrands) {
            this.searchEvent.emit({
              loadBrands: false,
              loadProps: true,
              loadCount: false
            });
          }
        }),
        takeUntil(this.cacnelSearchBrands$)
      )
      .subscribe(() => {
        this.brandListLoading = false;
      });

    // this.retailRange = [resCount.min_retail, resCount.max_retail];
    // this.selected_range_retail = [resCount.min_retail, resCount.max_retail];
  }

  onBrandSelect(selectedBrands: Brand[]) {
    this.selectedBrands = selectedBrands;
    this.searchEvent.emit({
      loadBrands: false,
      loadProps: false,
      loadCount: true
    });
    return;
  }

  searchByPage(page = 0) {
    this.loadingProducts = true;
    this.cacnelSearch$.next(true);
    this.searchServiceSubscription = this.productService
      .search(
        this.buildSearchPayload({
          limit: this.pageSize,
          offset: page * this.pageSize,
          sort_active: this.sort_active,
          sort_direction: this.sort_direction
        })
      )
      .pipe(
        map((res) => {
          this.pageLoaded += 1;
          this.products = this.products.concat(res.data);
        }),
        takeUntil(this.cacnelSearch$)
      )
      .subscribe(() => {
        this.loadingProducts = false;
      });
  }

  sortData(event: any) {
    this.sort_active = event.active;
    this.sort_direction = event.direction;
    this.searchEvent.emit({
      loadBrands: false,
      loadProps: false,
      loadCount: false
    });
  }

  clear() {
    this.selectedBrands = [];
    this.selectedFolder = undefined;
    this.selectedProperties = [];
    this.searchControl.setValue('');
    this.onlyInStockControl.setValue(false);
    this.withoutAddressControl.setValue(false);
    this.withAddressDebtControl.setValue(false);
    this.onlyBufferStockControl.setValue(false);
    this.withoutPlacedStockControl.setValue(false);
    this.multiCellOnlyControl.setValue(false);
    this.withoutBarcodeControl.setValue(false);
    this.withoutFolderControl.setValue(false);
    this.withoutImageControl.setValue(false);
    this.withoutPropsControl.setValue(false);
    this.products = [];
    this.clearEvent.emit();
    window.dispatchEvent(new Event('clearSearchControl'));
  }

  onPropsSelect(payload: { property: Property; values: string[] }) {
    const foundPropertyIndex = this.selectedProperties.findIndex((selectedProperty) => {
      return selectedProperty.property.id == payload.property.id;
    });
    if (foundPropertyIndex > -1) {
      if (payload.values.length == 0) {
        this.removeProperty(foundPropertyIndex);
      } else {
        this.updateSelectedProperty(foundPropertyIndex, payload.values);
      }
    } else {
      this.addProperty(payload);
    }
    this.searchEvent.emit({
      loadBrands: false,
      loadProps: false,
      loadCount: true
    });
    return;
  }

  removeProperty(propertyIndex: number) {
    return this.selectedProperties.splice(propertyIndex, 1);
  }

  setNewSearchValue() {
    this.searchControl.setValue(this._searchControl.value || '');
    this.searchEvent.emit({
      loadBrands: true,
      loadProps: true,
      loadCount: true
    });
  }

  updateSelectedProperty(propertyIndex: number, values: string[]) {
    return (this.selectedProperties[propertyIndex].values = values);
  }

  addProperty(payload: { property: Property; values: string[] }) {
    return this.selectedProperties.push({
      property: payload.property,
      values: payload.values
    });
  }

  async loadProps() {
    this.propsListLoading = true;
    this.cacnelSearchProps$.next(true);
    this.propsSubscription = this.propertyService
      .searchByProductDto(this.buildSearchPayload({ limit: this.pageSize }))
      .pipe(
        map((res) => {
          this.availableProps = res;
          const prevProps = JSON.stringify(this.selectedProperties);
          this.selectedProperties = this.selectedProperties.filter((prop) => {
            const result = this.availableProps.find(
              (av_prop) => av_prop.property.id === prop.property.id
            );
            return result;
          });

          const newProps = JSON.stringify(this.selectedProperties);

          if (prevProps !== newProps) {
            this.searchEvent.emit({
              loadBrands: false,
              loadProps: false,
              loadCount: false
            });
          }
        }),
        takeUntil(this.cacnelSearchProps$)
      )
      .subscribe(() => {
        this.propsListLoading = false;
      });
  }

  private buildSearchPayload(extra: Record<string, unknown> = {}) {
    return {
      search_keys: this.searchControl.value,
      folder_id: this.selectedFolder?.id,
      brand_ids: this.selectedBrands.map((brand) => brand.id),
      only_in_stock: this.onlyInStockControl.value,
      without_address: this.withoutAddressControl.value,
      with_address_debt: this.withAddressDebtControl.value,
      only_buffer_stock: this.onlyBufferStockControl.value,
      without_placed_stock: this.withoutPlacedStockControl.value,
      multi_cell_only: this.multiCellOnlyControl.value,
      without_image: this.withoutImageControl.value,
      without_barcode: this.withoutBarcodeControl.value,
      without_folder: this.withoutFolderControl.value,
      without_props: this.withoutPropsControl.value,
      properties: this.selectedProperties.map((prop) => {
        return {
          property_id: prop.property.id,
          values: prop.values
        };
      }),
      ...extra
    };
  }

  create() {
    const dialogRef = this.dialog.open(CreateComponent);
    const subscription = dialogRef.componentInstance.createdEvent.subscribe((product) => {
      this.searchControl.setValue(`${product.article} ${product.brand.description}`);
      dialogRef.close();
      subscription.unsubscribe();
    });
  }
}
